import { ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { ApiError } from '@ps/api-client/http';
import { authApi, type AppName, type SessionUser } from '@ps/api-client/auth';
import { Alert } from './alert';
import { AuthLayout, TextButton, type AuthPoint } from './auth-layout';
import { Button } from './button';
import { useBlurValidation } from '../lib/use-blur-validation';
import { EMAIL_MESSAGE, isEmail } from '../lib/validate';
import { FieldError } from './field-error';
import { Input, Label } from './input';

// Two-step sign-in shared by all three apps (S-01 / P-C0): email + password, then the 6-digit authenticator code.
// The split layout follows docs/06 §4: white form left, forest panel right.

export interface LoginScreenProps {
  app: AppName;
  title: string;                       // e.g. "Compliance Console"
  signInTo?: React.ReactNode;          // customer app: "Sign in to: PayStreet Hong Kong"
  points: AuthPoint[];                 // value propositions on the forest panel
  eyebrow?: string;                    // forest panel: small label above the headline
  headline?: string;                   // forest panel: the promise, in Montserrat
  /** Customer app: show "Log in with iAM Smart" as a Phase 2 placeholder (prototype P-C0), disabled. */
  iamSmartPhase2?: boolean;
  /** Customer app: "Forgot your password?" under the password field. */
  onForgotPassword?: () => void;
  onSignedIn: (user: SessionUser) => void;
  /** Customer app: the password was right but the authenticator is not set up yet (the server opened set-up). */
  onEnrolmentRequired?: () => void;
  /** Customer app: the email address is not verified yet; offer a new link. */
  onResendVerification?: (email: string) => Promise<void>;
  footer?: React.ReactNode;
}

export function LoginScreen({ app, title, signInTo, points, eyebrow, headline, iamSmartPhase2, onForgotPassword, onSignedIn, onEnrolmentRequired, onResendVerification, footer }: LoginScreenProps) {
  const [step, setStep] = React.useState<'password' | 'mfa'>('password');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [unverified, setUnverified] = React.useState(false);
  const [resent, setResent] = React.useState(false);

  const v = useBlurValidation({
    email: () => (!email.trim() ? 'Enter your email address' : !isEmail(email) ? EMAIL_MESSAGE : null),
    password: () => (!password ? 'Enter your password' : null),
  });
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.body.message : 'Something went wrong. Please try again.');

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!v.touchAll()) return;
    setBusy(true); setError(null); setUnverified(false); setResent(false);
    try {
      const r = await authApi.login(app, email.trim(), password);
      setPassword(''); v.reset();
      if (r.next === 'ENROL_MFA') {
        if (onEnrolmentRequired) onEnrolmentRequired();
        else setError('Your account is not set up for this app.');
      } else setStep('mfa');
    } catch (err) {
      fail(err);
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED') setUnverified(true);
    } finally { setBusy(false); }
  }

  async function resend() {
    if (!onResendVerification) return;
    if (!isEmail(email)) { v.touch('email'); return; }
    setBusy(true);
    try { await onResendVerification(email); setResent(true); setError(null); }
    catch (err) { fail(err); }
    finally { setBusy(false); }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { const r = await authApi.verifyMfa(app, code); onSignedIn(r.user); }
    catch (err) {
      fail(err);
      setCode('');
      if (err instanceof ApiError && err.status === 401 && err.code !== 'INVALID_MFA') setStep('password');
    } finally { setBusy(false); }
  }

  return (
    <AuthLayout title={title} intro={signInTo} points={points} eyebrow={eyebrow} headline={headline} footer={step === 'password' ? footer : undefined}>
      {error && (
        <Alert tone="bad" className="mt-6">
          {error}
          {unverified && onResendVerification && <div className="mt-2"><TextButton onClick={resend} disabled={busy}>Send me a new link</TextButton></div>}
        </Alert>
      )}
      {resent && <Alert tone="ok" className="mt-6">If this address needs verifying, a new link is on its way. Check your inbox.</Alert>}

      {step === 'password' ? (
        <form className="mt-6 space-y-4" onSubmit={submitPassword} noValidate>
          {iamSmartPhase2 && (
            <>
              <button type="button" disabled title="Coming in a later phase"
                className="h-10 w-full rounded-full border border-tick/40 text-sm font-semibold text-tick-ink/50">Log in with iAM Smart <span className="font-normal text-ink-3">· Phase 2</span></button>
              <p className="flex items-center gap-3 text-xs text-ink-3" aria-hidden><span className="h-px flex-1 bg-line" />or<span className="h-px flex-1 bg-line" /></p>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} {...v.field('email', 'email')} />
            <FieldError id="email" error={v.errorOf('email')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} {...v.field('password', 'password')} />
            <FieldError id="password" error={v.errorOf('password')} />
          </div>
          {onForgotPassword && <TextButton onClick={onForgotPassword}>Forgot your password?</TextButton>}
          <Button type="submit" size="lg" className="w-full" disabled={busy}>{busy ? 'Checking…' : 'Continue'}</Button>
        </form>
      ) : (
        <form className="mt-6 space-y-4" onSubmit={submitCode} noValidate>
          <p className="flex items-start gap-2 text-ink-2"><ShieldCheck className="mt-0.5 size-4 shrink-0 text-tick" aria-hidden />Enter the current 6-digit code from your authenticator app within 5 minutes.</p>
          <div className="space-y-1.5">
            <Label htmlFor="code">Authentication code</Label>
            <Input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required autoFocus
              className="font-mono text-lg tracking-[0.3em]" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Sign in'}</Button>
          <button type="button" className="w-full text-sm font-semibold text-tick-ink hover:underline" onClick={() => { setStep('password'); setError(null); }}>Use a different account</button>
        </form>
      )}
    </AuthLayout>
  );
}
